// Vault network layer — the ONLY place that calls apiCall for the vault domain.
import { apiCall } from '@/lib/api-client';
import { VAULT_PAGE_SIZE } from './limits';
import type {
  ListDocumentsPageParams,
  VaultAuditPage,
  VaultDocument,
  VaultDocumentDetail,
  VaultDocumentPage,
  VaultFolderDocumentsPage,
} from './types';

export interface ListDocumentsParams {
  productId?: number;
  type?: string;
}

export async function listDocuments(
  params: ListDocumentsParams = {},
): Promise<VaultDocument[]> {
  const qs = new URLSearchParams();
  if (params.productId != null) qs.set('product_id', String(params.productId));
  if (params.type) qs.set('type', params.type);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall<VaultDocument[]>(`/api/vault/documents${suffix}`);
}

/** Paginated library — the scalable read path (server-side filter/sort/rollup). */
export async function getDocumentsPage(
  params: ListDocumentsPageParams & { offset?: number; limit?: number },
): Promise<VaultDocumentPage> {
  const qs = new URLSearchParams();
  if (params.search?.trim()) qs.set('search', params.search.trim());
  if (params.domain) qs.set('domain', params.domain);
  if (params.type) qs.set('type', params.type);
  if (params.status) qs.set('status', params.status);
  if (params.sort) qs.set('sort', params.sort);
  if (params.dir) qs.set('dir', params.dir);
  qs.set('offset', String(params.offset ?? 0));
  qs.set('limit', String(params.limit ?? 10));
  return apiCall<VaultDocumentPage>(`/api/vault/documents/page?${qs}`);
}

export async function getDocument(ref: string): Promise<VaultDocumentDetail> {
  return apiCall<VaultDocumentDetail>(`/api/vault/documents/${ref}`);
}

/** One cursor page of the audit trail (newest first), 10 rows/page. `before` =
 *  the previous page's `next_cursor`. */
// The vault's one page size — see limits.ts. The trail used to load 10 at a time
// while every list beside it loaded a different number.
const AUDIT_PAGE_SIZE = VAULT_PAGE_SIZE;
export async function getDocumentAudit(ref: string, before?: number): Promise<VaultAuditPage> {
  const params = new URLSearchParams({ limit: String(AUDIT_PAGE_SIZE) });
  if (before != null) params.set('before', String(before));
  return apiCall<VaultAuditPage>(`/api/vault/documents/${ref}/audit?${params}`);
}

export interface AuditVerifyResult {
  ok: boolean;
  checked: number;
  first_broken_id: number | null;
  head_hash: string;
}

/** Re-walk the tamper-evident audit hash chain (admin-only). */
export async function verifyAudit(): Promise<AuditVerifyResult> {
  return apiCall<AuditVerifyResult>('/api/vault/audit/verify');
}

export interface TransitionBody {
  to_state: string;
  reason?: string;
  password?: string;
}

export async function transitionDocument(
  ref: string,
  body: TransitionBody,
): Promise<VaultDocumentDetail> {
  return apiCall<VaultDocumentDetail>(
    `/api/vault/documents/${encodeURIComponent(ref)}/transition`,
    { method: 'POST', json: body },
  );
}

export async function deleteRun(ref: string, runId: number): Promise<VaultDocumentDetail> {
  return apiCall<VaultDocumentDetail>(
    `/api/vault/documents/${encodeURIComponent(ref)}/runs/${runId}`,
    { method: 'DELETE' },
  );
}

export interface UploadInput {
  file: File;
  title: string;
  doc_type: string;
  domain: string;
  /** Market this document is filed for (US/EU/UK/NI/AU/CA); '' = global. */
  country: string;
}

/** Upload a customer's own document into the vault (multipart). */
export async function uploadDocument(input: UploadInput): Promise<VaultDocumentDetail> {
  const fd = new FormData();
  fd.append('file', input.file);
  fd.append('title', input.title);
  fd.append('doc_type', input.doc_type);
  fd.append('domain', input.domain);
  fd.append('country', input.country);
  return apiCall<VaultDocumentDetail>('/api/vault/uploads', {
    method: 'POST',
    bodyRaw: fd,
  });
}

// --- Bulk upload (many files in one flow) --------------------------------------
// Two transports, one result shape. The primary path is a direct browser→S3 upload
// (presign → PUT to S3 → finalize) so bytes never stream through the app; when the
// backend can't presign (dev), presign returns fallback=true and we POST the files
// through the app's /uploads/batch instead. Both return VaultBulkUploadResult[].

/** Shared metadata applied to every file in a bulk upload. */
export interface BulkUploadMeta {
  doc_type: string;
  domain: string;
  /** Market this document is filed for (US/EU/UK/NI/AU/CA); '' = global. */
  country: string;
  /** When set, the batch lands in this product's folder (name locked to the product). */
  product_id?: number | null;
  /** When set, the batch lands in this folder NODE (tree "upload into this folder"). */
  folder_id?: number | null;
  /** No destination was chosen (the Vault root / All files upload button with the folder field
   *  left empty) → the files land LOOSE at the vault root. Without it the server resolves a
   *  folder from doc_type, which find-or-creates one named "Document" and files them inside —
   *  off the very page they were uploaded from, and that is how an upload reads as lost. */
  to_root?: boolean;
}

export interface VaultBulkUploadResult {
  filename: string;
  /** 'skipped': a folder upload's OS junk (.DS_Store, __MACOSX/…) — nothing stored, not an error. */
  status: 'ok' | 'failed' | 'skipped';
  ref: string | null;
  document_number: string | null;
  error: string | null;
  /** Why a file was skipped — a folder upload's file already in that folder. */
  note?: string | null;
}

export interface VaultBulkUploadResponse {
  results: VaultBulkUploadResult[];
  ok_count: number;
  failed_count: number;
}

/** An S3 presigned POST target: the browser POSTs `fields` + the file to `url`. */
export interface PresignedPost {
  url: string;
  fields: Record<string, string>;
}

export interface VaultPresignItem {
  upload_id: number;
  ref: string;
  filename: string;
  storage_key: string;
  presigned: PresignedPost;
  content_type: string;
  max_bytes: number;
}

export interface VaultPresignResponse {
  /** true → the backend can't presign (dev); use uploadDocumentsBatch instead. */
  fallback: boolean;
  uploads: VaultPresignItem[];
  /** Positions in the REQUEST's file list the server reserved nothing for (OS junk on a folder
   *  upload). `uploads` is paired with the sent files BY POSITION, so the manager walks these
   *  to keep every later file on its own reservation. */
  skipped_indices?: number[];
  /** The skipped positions that carry a reason for the person (a folder upload's unchanged file);
   *  positions without one are OS junk. */
  skipped?: { index: number; reason: string }[];
}

/** How many documents of each kind the vault holds — the rail's group counts, and the total
 *  behind "All files". Counted the way the library LISTS them, so the two always agree. */
export interface VaultTypeCounts {
  total: number;
  by_type: Record<string, number>;
}

export async function getDocumentTypeCounts(): Promise<VaultTypeCounts> {
  return apiCall<VaultTypeCounts>('/api/vault/documents/type-counts');
}

/** Which of these names the destination already holds — asked before anything uploads so the
 *  dialog can flag a clash rather than surprise someone with a renamed file afterwards. */
export async function checkUploadNames(
  filenames: string[],
  dest: { folderId?: number | null; toRoot?: boolean },
): Promise<string[]> {
  const res = await apiCall<{ taken: string[] }>('/api/vault/uploads/name-check', {
    method: 'POST',
    json: { filenames, folder_id: dest.folderId ?? null, to_root: !!dest.toRoot },
  });
  return res.taken;
}

/** The path the browser reports for a file picked as part of a FOLDER, relative to that folder
 *  ("Dossier/Section 1/a.pdf"); empty for a plain file pick. Sent so the server rebuilds the tree.
 *  `webkitRelativePath` is a WebKit-prefixed name every current browser implements. */
export function relativePathOf(file: File): string | null {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
  return rel && rel !== file.name ? rel : null;
}

/** Reserve direct-to-S3 upload targets for a batch of files. Each file carries its
 *  folder-relative path (if it came from a folder pick) so the server files it in the right
 *  subfolder — a folder upload is just a loose upload that also says where each file goes. */
export async function presignUploads(
  files: File[],
  meta: BulkUploadMeta,
): Promise<VaultPresignResponse> {
  return apiCall<VaultPresignResponse>('/api/vault/uploads/presign', {
    method: 'POST',
    json: {
      // size: lets the server skip an unchanged file (same folder, name and size) before any bytes move.
      files: files.map((f) => ({ filename: f.name, relative_path: relativePathOf(f), size_bytes: f.size })),
      ...meta,
    },
  });
}

/** Validate + promote the reserved uploads whose bytes have landed in S3.
 *
 *  Takes the session's cancel signal because THIS is the step that decides whose vault a file
 *  lands in: the bytes reach storage under a signature, but the document is created for whoever
 *  this request is authenticated as. Aborting the transfer alone left a file that had finished
 *  moving still being filed after the person signed out. */
export async function finalizeUploads(
  uploadIds: number[],
  signal?: AbortSignal,
): Promise<VaultBulkUploadResponse> {
  return apiCall<VaultBulkUploadResponse>('/api/vault/uploads/finalize', {
    method: 'POST',
    json: { upload_ids: uploadIds },
    signal,
  });
}

/** Through-app fallback: POST all files in one multipart request.
 *
 *  Takes the session's cancel signal for the same reason finalizeUploads does, and more so: this
 *  single request carries the bytes AND creates the documents, so nothing else stands between
 *  signing out and a file landing in a vault. */
export async function uploadDocumentsBatch(
  files: File[],
  meta: BulkUploadMeta,
  signal?: AbortSignal,
): Promise<VaultBulkUploadResponse> {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  // One entry per file, positionally ('' = no folder path), so the dev/through-app fallback
  // rebuilds a folder pick's tree exactly as the presigned path does. Two paths, one behaviour.
  files.forEach((f) => fd.append('relative_paths', relativePathOf(f) ?? ''));
  fd.append('doc_type', meta.doc_type);
  fd.append('domain', meta.domain);
  fd.append('country', meta.country);
  if (meta.product_id != null) fd.append('product_id', String(meta.product_id));
  if (meta.folder_id != null) fd.append('folder_id', String(meta.folder_id));
  if (meta.to_root) fd.append('to_root', 'true');
  return apiCall<VaultBulkUploadResponse>('/api/vault/uploads/batch', {
    method: 'POST',
    signal,
    bodyRaw: fd,
  });
}

// --- Folders (uploads grouped by folder name) ---------------------------------

export interface VaultFolder {
  name: string;
  count: number;
  updated_at: string | null;
  /** Dominant domain of the folder's files: device|ivd|drug|biologic|other. */
  domain: string;
  /** Set when the folder is tied to a product. */
  product_id?: number | null;
  product_name?: string | null;
  /** Folder node id + parent (present on create; used for tree placement / subfolder ops). */
  id?: number | null;
  parent_id?: number | null;
}

/** Upload folders for this customer, each with a file count (newest-active first). */
export async function getVaultFolders(): Promise<VaultFolder[]> {
  const res = await apiCall<{ folders: VaultFolder[] }>('/api/vault/folders');
  return res.folders;
}

/** Create an empty folder (or return the existing case-insensitive sibling match). With a
 *  productId, the folder is named after + linked to that product; with a parentId, it's created
 *  INSIDE that folder (a folder-in-a-folder). */
export async function createFolder(
  name: string, productId?: number | null, parentId?: number | null,
): Promise<VaultFolder> {
  return apiCall<VaultFolder>('/api/vault/folders', {
    method: 'POST',
    json: { name, product_id: productId ?? null, parent_id: parentId ?? null },
  });
}

// --- Folder tree (nested) + archive import ------------------------------------

export interface VaultFolderNode {
  id: number;
  parent_id: number | null;
  name: string;
  count: number;            // DIRECT live files in this node
  domain: string;
  product_id: number | null;
  product_name: string | null;
  /** Newest direct file in the folder (its own creation when empty) — lets the folder list
   *  honour the same "recently updated" sort as the file list. */
  updated_at: string | null;
  /** The linked product's own documents, which this folder SHOWS but does not hold. Separate
   *  from `count` deliberately: that one is what the delete confirmation promises to remove,
   *  and these are not filed here, so deleting the folder leaves them alone. */
  generated_count: number;
}

export interface VaultFolderTree {
  nodes: VaultFolderNode[];
  root_file_count: number;  // loose files at the vault root ("All files")
}

/** The customer's nested folder tree (nodes + parent links + direct counts). */
export async function getFolderTree(): Promise<VaultFolderTree> {
  return apiCall<VaultFolderTree>('/api/vault/folders/tree');
}

/** One page of the direct files in one folder NODE (by id) — the tree view's contents.
 *  Search and sort are SERVER-side: the view holds one page, so filtering in the browser would
 *  search only what happens to be loaded and miss files on later pages. */
export async function getFolderDocumentsById(
  folderId: number,
  params: { search?: string; sort?: string; dir?: string; offset?: number; limit?: number } = {},
): Promise<VaultFolderDocumentsPage> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.sort) qs.set('sort', params.sort);
  if (params.dir) qs.set('dir', params.dir);
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return apiCall<VaultFolderDocumentsPage>(
    `/api/vault/folders/${folderId}/documents${q ? `?${q}` : ''}`);
}

/** One keyset page of the loose files at the vault ROOT (folder_id IS NULL) — the "Vault root"
 *  view. `next_cursor` is the upload id to pass back as `cursor` for the next page (null = last). */
export interface VaultRootDocumentsPage {
  items: VaultDocument[];
  next_cursor: number | null;
}

export async function getRootDocuments(
  params?: { cursor?: number; limit?: number; search?: string },
): Promise<VaultRootDocumentsPage> {
  const qs = new URLSearchParams();
  if (params?.cursor != null) qs.set('cursor', String(params.cursor));
  if (params?.search) qs.set('search', params.search);
  qs.set('limit', String(params?.limit ?? 25));
  return apiCall<VaultRootDocumentsPage>(`/api/vault/folders/root/documents?${qs}`);
}

export interface ArchiveSkippedItem {
  name: string;
  reason: string;
}

export interface ArchiveImportStatus {
  id: number;
  status: 'pending' | 'importing' | 'done' | 'failed';
  filename: string;
  files_created: number;
  folders_touched: number;
  skipped_count: number;              // true total skipped
  skipped: ArchiveSkippedItem[];      // detail (capped; may be fewer than skipped_count)
  replaced_count: number;             // files whose earlier copy this import RETIRED
  replaced: string[];                 // their names (capped, same as skipped)
  error: string | null;
}

/** Upload a zip/tar to be unzipped into the vault (returns the pending job to poll). With
 *  ``parentFolderId`` the tree is built UNDER that folder; ``domain`` tags every extracted file. */
export async function importArchive(
  file: File, parentFolderId?: number | null, domain?: string, signal?: AbortSignal,
): Promise<ArchiveImportStatus> {
  const fd = new FormData();
  fd.append('file', file);
  if (parentFolderId != null) fd.append('parent_folder_id', String(parentFolderId));
  if (domain) fd.append('domain', domain);
  // Carries the session's cancel signal because this request IS the upload: the zip streams in
  // its body. Checking a flag beforehand is not enough — a sign-out that happens while the body
  // is still going up would otherwise leave the request to complete and the server to accept the
  // import. Once the server has the whole file the import is its job and will finish; the signal
  // is what stops it getting there.
  return apiCall<ArchiveImportStatus>('/api/vault/archives', { method: 'POST', bodyRaw: fd, signal });
}

/** Poll one archive-import job. */
export async function getArchiveImport(id: number): Promise<ArchiveImportStatus> {
  return apiCall<ArchiveImportStatus>(`/api/vault/archives/${id}`);
}

/** Poll MANY archive-import jobs in ONE request — what the upload tray watches a batch with.
 *  Asking per job cost one request per zip per second, so a big batch out-asked the whole app.
 *  Ids that are not the caller's are simply absent from the answer. */
export async function getArchiveImports(ids: number[]): Promise<ArchiveImportStatus[]> {
  if (ids.length === 0) return [];
  const res = await apiCall<{ imports: ArchiveImportStatus[] }>(
    `/api/vault/archives?ids=${ids.join(',')}`,
  );
  return res.imports ?? [];
}

// --- Folder-node operations (by id — work on any tree node, incl. nested) ---------------------

export async function renameFolderNode(folderId: number, name: string): Promise<VaultFolder> {
  return apiCall<VaultFolder>(`/api/vault/folder-nodes/${folderId}/rename`, { method: 'POST', json: { name } });
}

export async function linkFolderNodeToProduct(folderId: number, productId: number): Promise<void> {
  await apiCall(`/api/vault/folder-nodes/${folderId}/link-product`, { method: 'POST', json: { product_id: productId }, allow204: true });
}

/** Remove a folder node's product link (the folder + its files are untouched). */
export async function unlinkFolderNode(folderId: number): Promise<void> {
  await apiCall(`/api/vault/folder-nodes/${folderId}/unlink-product`, { method: 'POST', allow204: true });
}

export async function setFolderNodeDomain(folderId: number, domain: string): Promise<void> {
  await apiCall(`/api/vault/folder-nodes/${folderId}/domain`, { method: 'POST', json: { domain }, allow204: true });
}

export async function deleteFolderNode(
  folderId: number, opts?: { onlyIfEmpty?: boolean },
): Promise<void> {
  // onlyIfEmpty: the server refuses (409) to delete a folder that still holds files or subfolders,
  // instead of cascading. The automated caller (a failed upload discarding the folder it made) sets
  // it; the user-driven delete leaves it off and confirms in its own dialog.
  const q = opts?.onlyIfEmpty ? '?only_if_empty=true' : '';
  await apiCall(`/api/vault/folder-nodes/${folderId}${q}`, { method: 'DELETE', allow204: true });
}

/** Reparent a folder node under another folder (newParentId null = the vault root). */
export async function moveFolderNode(folderId: number, newParentId: number | null): Promise<VaultFolder> {
  return apiCall<VaultFolder>(`/api/vault/folder-nodes/${folderId}/move`, {
    method: 'POST', json: { new_parent_id: newParentId },
  });
}

/** Move an upload into a folder node by id (folderId null = the vault root / loose). */
export async function moveUploadToFolder(uploadId: number, folderId: number | null): Promise<void> {
  await apiCall(`/api/vault/uploads/${uploadId}/move-to-folder`, {
    method: 'POST', json: { folder_id: folderId }, allow204: true,
  });
}

/**
 * Same-origin, cookie-authed URL that downloads a folder node's whole subtree as a zip.
 *
 * The server streams the archive as it builds it, so this is a plain link: the browser's own
 * download manager takes it straight away, exactly like any other file download. There is nothing
 * to poll and nothing to store.
 */
export function folderNodeDownloadUrl(folderId: number): string {
  return `/api/vault/folder-nodes/${folderId}/download`;
}

/** Delete one uploaded document. */
export async function deleteUpload(uploadId: number): Promise<void> {
  await apiCall(`/api/vault/uploads/${uploadId}`, { method: 'DELETE', allow204: true });
}

/** POST a file straight to S3 via a presigned POST, reporting upload progress. Uses
 *  XHR (not fetch) because only XHR exposes upload progress events. Resolves on a 2xx
 *  from S3, rejects otherwise. The `fields` MUST be appended before the file. */
export function putToS3WithProgress(
  presigned: PresignedPost,
  file: File,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    Object.entries(presigned.fields).forEach(([k, v]) => fd.append(k, v));
    fd.append('file', file); // the file part must come last for an S3 POST policy
    const xhr = new XMLHttpRequest();
    xhr.open('POST', presigned.url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload to storage failed (${xhr.status}).`));
    xhr.onerror = () => reject(new Error('Upload to storage failed.'));
    // Signing out must stop bytes that are already moving, not merely hide the tray: this
    // transfer goes straight to storage under a signature minted for the person who started
    // it, so it would otherwise keep running for whoever sits down next.
    if (signal) {
      if (signal.aborted) { reject(new Error('Upload cancelled.')); return; }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.onabort = () => reject(new Error('Upload cancelled.'));
    }
    xhr.send(fd);
  });
}

/** Same-origin, cookie-authed download URL for an anchor (not a fetch). */
export function downloadUrl(ref: string, versionId?: number): string {
  const v = versionId != null ? `?version=${versionId}` : '';
  return `/api/vault/documents/${ref}/download${v}`;
}

/** Same-origin inline URL — renders the PDF in place (the in-app preview iframe)
 *  instead of downloading. Audited as a view; framable same-origin only. */
export function previewUrl(ref: string, versionId?: number): string {
  const params = new URLSearchParams({ inline: '1' });
  if (versionId != null) params.set('version', String(versionId));
  return `/api/vault/documents/${ref}/download?${params.toString()}`;
}

/** Resolve an exact document number to the one document it names, so the search box can jump
 *  straight to it. Rejects (404) if the number doesn't exist for this tenant. */
export function resolveDocumentNumber(
  number: string,
): Promise<{ ref: string; document_number: string }> {
  return apiCall(`/api/vault/documents/resolve?number=${encodeURIComponent(number)}`);
}
